import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { AlertCircle, CheckCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { aiAgentClient } from "@/domains/ai/ai-chat/api/ai-agent-client";
import { useClientToolsStore } from "@/domains/ai/ai-chat/store/client-tools-store";
import { useAiChatStore } from "@/domains/ai/ai-chat/stores/ai-chat.store";
import { useSalonId } from "@/lib/store/salon";

interface ActionRequest {
  name: string;
  args: Record<string, any>;
  description?: string;
}

interface ConfirmationCardArgs {
  description: string;
  decisions: string[];
  actionRequests: ActionRequest[];
}

/**
 * ConfirmationCard Component
 *
 * Displays a confirmation UI for batch operations (interrupts from backend)
 * Shows description, action requests, and decision buttons (approve/edit/reject)
 */
export const ConfirmationCard: ToolCallMessagePartComponent = ({ args, result }) => {
  const salonId = useSalonId();
  const threadId = useAiChatStore((state) => state.threadId);
  const { handleStreamEvent, setIsRunning } = useAiChatStore();
  const toolReferences = useClientToolsStore((state) => state.getToolReferences());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Parse args
  const cardArgs = typeof args === "string" ? JSON.parse(args) : (args as ConfirmationCardArgs);
  const { description, decisions, actionRequests } = cardArgs;

  // Check if already resolved
  const hasResult = result !== undefined;

  const handleDecision = (decision: string) => {
    if (isSubmitting || hasResult) return;
    setIsSubmitting(true);
    setIsRunning(true);

    // Resume with decision
    aiAgentClient.resume(
      salonId,
      threadId,
      { type: "decision", decision: decision as "approve" | "reject" | "edit" },
      toolReferences,
      (event) => {
        if (event.type === "done") {
          setIsRunning(false);
          setIsSubmitting(false);
          return;
        }

        if (event.type === "error") {
          setIsRunning(false);
          setIsSubmitting(false);
          return;
        }

        // Handle all other events
        handleStreamEvent(event);
      }
    );
  };

  return (
    <Card className="my-2 max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {hasResult ? (
            <CheckCircle className="size-4 text-green-600" />
          ) : (
            <AlertCircle className="size-4 text-amber-600" />
          )}
          {hasResult ? "Confirmed" : "Confirmation Required"}
        </CardTitle>
        {description && (
          <CardDescription className="whitespace-pre-wrap">{description}</CardDescription>
        )}
      </CardHeader>

      {!hasResult && actionRequests && actionRequests.length > 0 && (
        <CardContent className="space-y-3">
          {/* Action requests summary */}
          <div className="text-sm">
            <p className="mb-2 font-medium">Actions to perform ({actionRequests.length}):</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {actionRequests.slice(0, 5).map((req, idx) => (
                <li key={`${req.name}-${idx}`} className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>
                    <span className="font-medium">{req.name}</span>
                    {req.args && Object.keys(req.args).length > 0 && (
                      <span className="ml-1">({Object.keys(req.args).length} params)</span>
                    )}
                  </span>
                </li>
              ))}
              {actionRequests.length > 5 && (
                <li className="text-muted-foreground/70">
                  ... and {actionRequests.length - 5} more
                </li>
              )}
            </ul>
          </div>

          {/* Decision buttons */}
          {decisions && decisions.length > 0 && (
            <div className="flex gap-2 pt-2">
              {decisions.map((decision) => (
                <Button
                  key={decision}
                  size="sm"
                  variant={
                    decision === "approve"
                      ? "default"
                      : decision === "reject"
                        ? "destructive"
                        : "outline"
                  }
                  onClick={() => handleDecision(decision)}
                  disabled={isSubmitting}
                >
                  {decision.charAt(0).toUpperCase() + decision.slice(1)}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {hasResult && (
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {typeof result === "string" ? result : JSON.stringify(result)}
          </p>
        </CardContent>
      )}
    </Card>
  );
};
